import {
  FeatureName,
  HomeEntityType,
  useCurrentWorkspace,
  useFeatures,
  useIsFeatureEnabled,
  WorkspaceType,
} from '@gripp/shared-logic';
import { SearchIcon, SearchInput, tokens } from '@gripp/shared-ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { Navigate } from 'react-router-dom';
import { DEFAULT_TIME_RANGE, TimeRange } from '../types';
import { ActivityMap } from './activityMap';
import { CardStrip } from './cardStrip';
import { TimeRangeSelector } from './timeRangeSelector';

/**
 * HomeDashboardPage — top-level shell for the OEM Home Dashboard.
 *
 * Layout (design: gripp-os HomeOEM — solid header bar, full-canvas map below):
 *  - `home-dashboard-topbar` — solid white header: left = TimeRangeSelector,
 *    right = expandable client-side search.
 *  - `home-dashboard-map` (absoluteFill) — ActivityMap. Plots aggregated Location
 *    pins at rest; switches to clustered per-entity points when a card's plotting
 *    mode is active. `searchQuery` filters the visible markers by name.
 *  - `home-dashboard-cardstrip` (absolute bottom overlay) — the 6-card strip.
 *
 * Page-owned state: `range` (default 7D), `plotMode` (null = resting Locations),
 * and `searchQuery`. The plottable cards (Activity / Assets / Routines) toggle
 * `plotMode`; selecting the active one again clears it.
 *
 * Self-guard: ProtectedRoute only enforces `requiredPermission`, NOT the feature
 * flag or workspace type — so the page redirects to /activities when the flag is
 * off or the workspace is not OEM (covers direct-URL access).
 */
export const HomeDashboardPage = () => {
  const { t } = useTranslation();
  const workspace = useCurrentWorkspace();
  const isHomeDashboardEnabled = useIsFeatureEnabled(
    FeatureName.OemHomeDashboard
  );
  // Crop Circle is a precondition for the dashboard (see home.tsx landing): the
  // glass/tokenized chrome assumes the CC skin. Mirror the same three-condition
  // gate here so a direct-URL hit with CC off bounces instead of rendering a
  // broken page under the legacy Header.
  const isCropCircleEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );

  // Features must resolve before the self-guard can trust useIsFeatureEnabled:
  // it reads false while useFeatures() is still loading, so a direct /home hit
  // or refresh would otherwise bounce to /activities even when the flag is on.
  // Mirror app/home.tsx's landing gate — wait for BOTH workspace and features.
  const { data: features, isLoading: featuresLoading } = useFeatures();

  const [range, setRange] = useState<TimeRange>(DEFAULT_TIME_RANGE);
  const [plotMode, setPlotMode] = useState<HomeEntityType | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Toggle a card's plotting mode: selecting the active one again clears it.
  const handleSelectPlot = (entityType: HomeEntityType) => {
    setPlotMode((current) => (current === entityType ? null : entityType));
  };

  // While the workspace (undefined = no account yet, null = fetching) OR the
  // features are still resolving, do not redirect — wait for a definitive answer.
  const isResolving =
    workspace === undefined ||
    workspace === null ||
    featuresLoading ||
    !features;
  if (!isResolving) {
    if (
      !isHomeDashboardEnabled ||
      !isCropCircleEnabled ||
      workspace.type !== WorkspaceType.Oem
    ) {
      return <Navigate to="/activities" replace />;
    }
  }

  return (
    <View style={styles.root} testID="home-dashboard-page">
      {/* Header bar — solid white chrome holding the controls. */}
      <View style={styles.header} testID="home-dashboard-topbar">
        <TimeRangeSelector range={range} onChange={setRange} />
        <SearchControl
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder={t('home.search.placeholder', 'Search')}
        />
      </View>

      {/* Map + bottom card overlay fill the area below the header. */}
      <View style={styles.body}>
        <View
          style={styles.mapSlot}
          testID="home-dashboard-map"
          accessibilityLabel={t('home.map.label', 'Activity map')}
        >
          <ActivityMap range={range} plotMode={plotMode} searchQuery={searchQuery} />
        </View>

        {/* Active-plot indicator (clears the mode) — sits above the map. */}
        {plotMode && (
          <View style={styles.plotBannerWrap} pointerEvents="box-none">
            <Pressable
              style={styles.plotBanner}
              onPress={() => setPlotMode(null)}
              accessibilityRole="button"
              accessibilityLabel={t('home.plot.clear', 'Clear map plotting')}
              testID="home-dashboard-plot-clear"
            >
              <Text style={styles.plotBannerText}>
                {t(`home.plot.${plotMode.toLowerCase()}`, plotMode)}
              </Text>
              <Text style={styles.plotBannerClose}>✕</Text>
            </Pressable>
          </View>
        )}

        <View style={styles.cardStripWrap} pointerEvents="box-none">
          <CardStrip
            range={range}
            plotMode={plotMode}
            onSelectPlot={handleSelectPlot}
          />
        </View>
      </View>
    </View>
  );
};

/**
 * SearchControl — the header's collapsed search button that expands to the Crop
 * Circle `SearchInput` (design: gripp-os HomePage collapsed search). SearchInput
 * owns the search icon, clear button, and web outline suppression, so this only
 * has to manage the collapsed↔expanded toggle: pressing the icon opens + focuses
 * the field; blurring it while empty collapses back to the icon.
 */
const SearchControl = ({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) => {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);

  if (!open && !value) {
    return (
      <Pressable
        style={[styles.searchButton, hovered && styles.searchButtonHovered]}
        onHoverIn={() => setHovered(true)}
        onHoverOut={() => setHovered(false)}
        onPress={() => setOpen(true)}
        accessibilityRole="button"
        accessibilityLabel={placeholder}
        testID="home-dashboard-search"
      >
        <SearchIcon size={20} color={tokens.colors.neutral[800]} />
      </Pressable>
    );
  }

  return (
    <SearchInput
      value={value}
      onChangeText={onChange}
      placeholder={placeholder}
      autoFocus
      height={36}
      width={220}
      onBlur={() => {
        if (!value) setOpen(false);
      }}
      testID="home-dashboard-search-field"
    />
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: tokens.colors.brand.neutral,
    overflow: 'hidden',
  },
  // Solid white header chrome (design: --card on a --border-section divider).
  header: {
    height: tokens.spacing['7xl'], // 56px
    paddingHorizontal: tokens.spacing.lg,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: tokens.colors.brand.white,
    borderBottomWidth: 1,
    borderBottomColor: tokens.colors.slate[100],
    zIndex: 10,
  },
  body: {
    flex: 1,
    position: 'relative',
    overflow: 'hidden',
  },
  mapSlot: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: tokens.colors.brand.neutral,
  },
  searchButton: {
    width: 36,
    height: 36,
    borderRadius: tokens.borderRadius.full,
    alignItems: 'center',
    justifyContent: 'center',
  },
  searchButtonHovered: {
    backgroundColor: tokens.colors.neutral[200],
  },
  plotBannerWrap: {
    position: 'absolute',
    top: tokens.spacing.md,
    left: tokens.spacing.lg,
    zIndex: 10,
  },
  plotBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
    paddingVertical: tokens.spacing.xs,
    paddingHorizontal: tokens.spacing.md,
    borderRadius: tokens.borderRadius.full,
    backgroundColor: tokens.colors.neutral[800],
  },
  plotBannerText: {
    ...tokens.typography.label.medium,
    fontWeight: '600',
    color: tokens.colors.brand.white,
  },
  plotBannerClose: {
    ...tokens.typography.label.medium,
    color: tokens.colors.neutral[400],
  },
  cardStripWrap: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 10,
  },
});
